import type { ReactNode } from 'react';
import { tw } from '@tw';

/**
 * One answer button, as every quiz in the product draws it: white paper on the
 * coloured card, its letter in the accent, and a single press state — the tile
 * dips and dims (`scale-[0.98] bg-white/70`). Deliberately no right/wrong
 * colour: a quiz here is a hook, not an exam, and the score arrives on the
 * result screen rather than under the finger.
 *
 * Three places drew this by hand — the storefront player, the landing player
 * and the landing's case wall — with identical strings, which is three chances
 * for a radius or a weight to drift and for one quiz to stop looking like the
 * others. It is one component now; the callers differ only in what they do
 * with the press.
 */
export function QuizOption({
  index,
  disabled,
  flashed,
  onPick,
  children,
}: {
  /** Position in the list — becomes the A/B/C letter. */
  index: number;
  disabled?: boolean;
  /** True for the tile that was just pressed. */
  flashed?: boolean;
  onPick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPick}
      className={tw(
        'mm-btn flex items-center gap-3 rounded-xl bg-white/95 px-4 py-3.5 text-left text-head-3 font-semibold text-[#16110f] transition-all',
        flashed && 'scale-[0.98] bg-white/70'
      )}
    >
      <span className="text-p-3 font-extrabold text-[var(--sf-accent-deep)]">
        {String.fromCharCode(65 + index)}
      </span>
      {children}
    </button>
  );
}
